import { X, Printer } from "lucide-react";

// Client-side map from our internal category → IRS Schedule C line item.
// Kept in sync with server-side categorization taxonomy at /api/export/schedule-c.
export const SCHEDULE_C_LINE = {
  meals: "Line 24b · Meals",
  office_supplies: "Line 22 · Supplies",
  electronics: "Line 22 · Supplies (Equipment)",
  transportation: "Line 9 · Car & Truck Expenses",
  groceries: "Line 24b · Meals (Client / Team)",
  software: "Line 48 · Other Expenses (Software)",
  travel: "Line 24a · Travel",
  entertainment: "Line 24b · Meals & Entertainment (partial)",
  other: "Line 48 · Other Expenses",
};

// Reusable render body — shared between the print-only sheet and the on-screen preview drawer.
function ScheduleCBody({ receipts, catLabels }) {
  const grouped = {};
  const nonDeductible = [];
  (receipts || []).forEach((r) => {
    if (!r.tax_deductible) { nonDeductible.push(r); return; }
    const line = SCHEDULE_C_LINE[r.category] || SCHEDULE_C_LINE.other;
    if (!grouped[line]) grouped[line] = [];
    grouped[line].push(r);
  });
  const orderedLines = Object.keys(grouped).sort();
  const grandTotal = orderedLines.reduce(
    (s, ln) => s + grouped[ln].reduce((ss, r) => ss + Number(r.total || 0), 0),
    0
  );

  return (
    <>
      <header className="pc-header">
        <h1>Schedule C — Deductible Receipt Summary</h1>
        <div className="pc-meta">
          <div><strong>Filer:</strong> Receipt Sheets export</div>
          <div><strong>Generated:</strong> {new Date().toLocaleString()}</div>
          <div><strong>Receipts in scope:</strong> {(receipts || []).length}</div>
        </div>
      </header>

      {orderedLines.map((line) => {
        const rows = grouped[line];
        const subtotal = rows.reduce((s, r) => s + Number(r.total || 0), 0);
        return (
          <div key={line} className="pc-block">
            <h2 className="pc-block-title">{line}</h2>
            <table className="pc-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Merchant</th>
                  <th>Category</th>
                  <th className="pc-num">Amount</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>{r.date}</td>
                    <td>{r.merchant}</td>
                    <td>{catLabels[r.category] || r.category}</td>
                    <td className="pc-num">${Number(r.total).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3}><strong>Subtotal · {line}</strong></td>
                  <td className="pc-num"><strong>${subtotal.toFixed(2)}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>
        );
      })}

      <div className="pc-grand">
        <span>Grand deductible total</span>
        <span className="pc-num"><strong>${grandTotal.toFixed(2)}</strong></span>
      </div>

      <section className="pc-appendix">
        <h2 className="pc-block-title">Appendix · Receipt Thumbnail Index</h2>
        <table className="pc-table pc-appendix-table">
          <thead>
            <tr>
              <th style={{ width: "72px" }}>Thumb</th>
              <th>Date</th>
              <th>Merchant</th>
              <th>Schedule C line</th>
              <th className="pc-num">Amount</th>
            </tr>
          </thead>
          <tbody>
            {(receipts || []).map((r) => {
              const line = r.tax_deductible
                ? (SCHEDULE_C_LINE[r.category] || SCHEDULE_C_LINE.other)
                : "— (personal)";
              return (
                <tr key={`app-${r.id}`}>
                  <td>
                    {r.image_url
                      ? <img src={r.image_url} alt="" className="pc-thumb" />
                      : <div className="pc-thumb-placeholder">n/a</div>}
                  </td>
                  <td>{r.date}</td>
                  <td>{r.merchant}</td>
                  <td>{line}</td>
                  <td className="pc-num">${Number(r.total).toFixed(2)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {nonDeductible.length > 0 && (
          <p className="pc-note">
            {nonDeductible.length} receipt(s) marked personal / non-deductible are shown
            in the appendix but excluded from Schedule C subtotals.
          </p>
        )}
      </section>
    </>
  );
}

// Hidden-until-print section. Uses the aggressive @media print stylesheet
// injected below to flatten the dark cockpit into a corporate white sheet.
export function PrintScheduleCSection({ receipts, catLabels }) {
  return (
    <>
      <section
        id="print-schedule-c"
        data-testid="print-schedule-c-view"
        className="print-only"
        aria-hidden="true"
      >
        <ScheduleCBody receipts={receipts} catLabels={catLabels} />
      </section>
      <PrintStyles />
    </>
  );
}

// On-screen preview drawer — lets users double-check line groupings before
// hitting the browser print dialog. Rendered directly against a WHITE canvas
// (so what you see is what will print) even inside the dark app shell.
export function PrintPreviewDrawer({ open, onClose, receipts, catLabels }) {
  if (!open) return null;
  return (
    <div
      data-testid="print-preview-overlay"
      className="fixed inset-0 z-[9700] flex items-start sm:items-center justify-center overflow-y-auto"
      style={{ background: "rgba(2,6,23,0.75)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="print-preview-drawer"
        className="relative bg-white text-black rounded-xl shadow-2xl my-8 mx-4 w-full max-w-4xl"
        style={{ border: "1px solid rgba(16,185,129,0.4)" }}
      >
        <div
          className="sticky top-0 z-10 flex items-center justify-between px-6 py-3 border-b border-slate-200 bg-white/95 backdrop-blur rounded-t-xl"
          data-print-hide
        >
          <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-emerald-700">
            Print Preview · Schedule C
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => window.print()}
              data-testid="preview-fire-print"
              className="h-8 px-3 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold flex items-center gap-1.5"
            >
              <Printer size={12} /> Send to Printer
            </button>
            <button
              onClick={onClose}
              data-testid="print-preview-close"
              aria-label="Close preview"
              className="h-8 w-8 rounded-md hover:bg-slate-100 text-slate-600 flex items-center justify-center"
            >
              <X size={14} />
            </button>
          </div>
        </div>
        <div id="print-schedule-c-preview" className="p-6 sm:p-8">
          <ScheduleCBody receipts={receipts} catLabels={catLabels} />
        </div>
      </div>
    </div>
  );
}

// Injected once — the aggressive @media print rewrite that unbreaks the
// white-on-white bug plus preview-drawer-mirrored typography.
function PrintStyles() {
  return (
    <style>{`
      .print-only { display: none; }
      /* On-screen preview inherits corporate typography from these rules too */
      #print-schedule-c-preview .pc-header { border-bottom: 2px solid #111; padding-bottom: 12px; margin-bottom: 20px; }
      #print-schedule-c-preview .pc-header h1 { font-size: 22px; font-weight: 800; margin: 0 0 6px; color: #000; }
      #print-schedule-c-preview .pc-meta { display: flex; gap: 20px; font-size: 11px; color: #333; font-family: ui-monospace, monospace; }
      #print-schedule-c-preview .pc-block { margin-bottom: 22px; }
      #print-schedule-c-preview .pc-block-title { font-size: 13px; text-transform: uppercase; letter-spacing: 0.16em; font-weight: 700; margin: 0 0 8px; padding-bottom: 4px; border-bottom: 1px solid #000; color: #000; }
      #print-schedule-c-preview .pc-table { width: 100%; border-collapse: collapse; font-size: 12px; color: #000; }
      #print-schedule-c-preview .pc-table th, #print-schedule-c-preview .pc-table td { padding: 6px 8px; border-bottom: 1px solid #e5e5e5; text-align: left; vertical-align: top; }
      #print-schedule-c-preview .pc-table thead th { background: #f2f2f2; font-size: 10px; text-transform: uppercase; letter-spacing: 0.14em; font-weight: 700; border-bottom: 1px solid #000; }
      #print-schedule-c-preview .pc-table tfoot td { border-top: 1px solid #000; border-bottom: none; padding-top: 8px; }
      #print-schedule-c-preview .pc-num { text-align: right; font-family: ui-monospace, monospace; white-space: nowrap; }
      #print-schedule-c-preview .pc-grand { display: flex; justify-content: space-between; align-items: baseline; padding: 12px 8px; margin: 16px 0 24px; border-top: 3px double #000; border-bottom: 3px double #000; font-size: 15px; font-weight: 700; color: #000; }
      #print-schedule-c-preview .pc-thumb { width: 56px; height: 56px; object-fit: cover; border: 1px solid #999; }
      #print-schedule-c-preview .pc-thumb-placeholder { width: 56px; height: 56px; border: 1px dashed #bbb; display: flex; align-items: center; justify-content: center; font-size: 9px; color: #999; text-transform: uppercase; letter-spacing: 0.14em; }
      #print-schedule-c-preview .pc-note { font-size: 10px; color: #555; margin-top: 10px; font-style: italic; }

      @media print {
        html, body {
          background: #ffffff !important;
          background-color: #ffffff !important;
          background-image: none !important;
          color: #000000 !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        body *, body *::before, body *::after {
          color: #000000 !important;
          text-shadow: none !important;
          -webkit-text-fill-color: #000000 !important;
          box-shadow: none !important;
          filter: none !important;
          backdrop-filter: none !important;
          -webkit-backdrop-filter: none !important;
        }
        .glass, [class*="glass"], [class*="bg-"],
        [style*="linear-gradient"], [style*="radial-gradient"], [style*="rgba("] {
          background: transparent !important;
          background-color: transparent !important;
          background-image: none !important;
          border-color: #cccccc !important;
        }
        nav, header, aside, .no-print, [data-print-hide] { display: none !important; }
        body > div > div:not(#print-schedule-c) { display: none !important; }
        #print-schedule-c { display: block !important; background: #ffffff !important; padding: 24px 28px; }
        #print-schedule-c .pc-header { border-bottom: 2px solid #111 !important; padding-bottom: 12px !important; margin-bottom: 20px !important; display: block !important; }
        #print-schedule-c .pc-header h1 { display: block !important; font-size: 22px !important; font-weight: 800 !important; margin: 0 0 6px !important; color: #000 !important; }
        #print-schedule-c .pc-meta { display: flex !important; gap: 20px !important; font-size: 11px !important; font-family: ui-monospace, monospace !important; }
        #print-schedule-c .pc-meta * { color: #333 !important; }
        #print-schedule-c .pc-block { display: block !important; margin-bottom: 22px; page-break-inside: avoid; }
        #print-schedule-c .pc-block-title { display: block !important; font-size: 13px !important; text-transform: uppercase !important; letter-spacing: 0.16em !important; font-weight: 700 !important; margin: 0 0 8px !important; padding-bottom: 4px !important; border-bottom: 1px solid #000 !important; color: #000 !important; }
        #print-schedule-c .pc-table { display: table !important; width: 100% !important; border-collapse: collapse !important; font-size: 11px !important; color: #000 !important; }
        #print-schedule-c .pc-table thead { display: table-header-group !important; }
        #print-schedule-c .pc-table tbody { display: table-row-group !important; }
        #print-schedule-c .pc-table tfoot { display: table-footer-group !important; }
        #print-schedule-c .pc-table tr { display: table-row !important; }
        #print-schedule-c .pc-table th,
        #print-schedule-c .pc-table td { display: table-cell !important; padding: 6px 8px !important; border-bottom: 1px solid #e5e5e5 !important; text-align: left !important; vertical-align: top !important; color: #000 !important; background: transparent !important; }
        #print-schedule-c .pc-table thead th { background: #f2f2f2 !important; font-size: 10px !important; text-transform: uppercase !important; letter-spacing: 0.14em !important; font-weight: 700 !important; border-bottom: 1px solid #000 !important; }
        #print-schedule-c .pc-table tfoot td { border-top: 1px solid #000 !important; border-bottom: none !important; padding-top: 8px !important; }
        #print-schedule-c .pc-num { text-align: right !important; font-family: ui-monospace, monospace !important; white-space: nowrap !important; }
        #print-schedule-c .pc-grand { display: flex !important; justify-content: space-between !important; align-items: baseline !important; padding: 12px 8px !important; margin: 16px 0 24px !important; border-top: 3px double #000 !important; border-bottom: 3px double #000 !important; font-size: 15px !important; font-weight: 700 !important; color: #000 !important; }
        #print-schedule-c .pc-appendix { display: block !important; page-break-before: always; }
        #print-schedule-c .pc-thumb { display: inline-block !important; width: 56px !important; height: 56px !important; object-fit: cover !important; border: 1px solid #999 !important; }
        #print-schedule-c .pc-thumb-placeholder { display: flex !important; width: 56px !important; height: 56px !important; border: 1px dashed #bbb !important; align-items: center !important; justify-content: center !important; font-size: 9px !important; color: #999 !important; text-transform: uppercase !important; letter-spacing: 0.14em !important; }
        #print-schedule-c .pc-note { display: block !important; font-size: 10px !important; color: #555 !important; margin-top: 10px !important; font-style: italic !important; }
        @page { margin: 14mm 12mm; }
      }
    `}</style>
  );
}
